import styled from "@emotion/styled";
import { Form } from "formik";
import theme from "@utils/theme";

const colors = {
  primary: theme.orange.orange500,
  primaryLight: theme.orange.orange100,
  primaryDark: theme.orange.orange600,
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  info: "#3b82f6",
  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  white: "#ffffff",
};

export const Container = styled.div`
  margin: 0 auto;
  background: ${colors.gray50};
  min-height: 100vh;
`;

export const FormContainer = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const LoadingFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Section = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${colors.gray200};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

export const SectionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${colors.primaryLight} 0%,
    ${colors.primary} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.2);
`;

export const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.gray800};
  margin: 0;
`;

export const SectionDescription = styled.p`
  font-size: 14px;
  color: ${colors.gray600};
  margin: 4px 0 0 0;
  line-height: 1.5;
`;

export const CurrentPlanBadge = styled.div<{
  type: "commission" | "subscription";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${(props) =>
    props.type === "commission"
      ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
      : `linear-gradient(135deg, ${colors.success} 0%, #059669 100%)`};
  color: ${colors.white};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const SubscriptionPlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  padding: 8px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const SubscriptionPlanCard = styled.div<{
  isPopular?: boolean;
  isCurrent?: boolean;
}>`
  background: ${colors.white};
  border: 2px solid
    ${(props) =>
      props.isPopular
        ? colors.primary
        : props.isCurrent
          ? colors.success
          : colors.gray200};
  border-radius: 16px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;

  ${(props) =>
    props.isPopular &&
    `
    transform: scale(1.02);
    box-shadow: 0 12px 40px rgba(249, 115, 22, 0.15);
  `}

  ${(props) =>
    props.isCurrent &&
    `
    border-color: ${colors.success};
    background: linear-gradient(135deg, ${colors.success}08 0%, ${colors.white} 100%);
    box-shadow: 0 8px 32px rgba(16, 185, 129, 0.15);
  `}

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) =>
      props.isPopular
        ? colors.primary
        : props.isCurrent
          ? colors.success
          : "transparent"};
  }
`;

export const PopularBadge = styled.div`
  position: absolute;
  top: 20px;
  right: -30px;
  background: ${colors.primary};
  color: ${colors.white};
  padding: 6px 40px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transform: rotate(45deg);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
`;

export const CurrentPlanBadgeCard = styled.div`
  position: absolute;
  top: 20px;
  left: 24px;
  background: ${colors.success};
  color: ${colors.white};
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
`;

export const PlanHeaderCard = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding-top: 8px;
`;

export const PlanIconCard = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    ${(props) => props.color} 0%,
    ${(props) => props.color}dd 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${colors.white};
  box-shadow: 0 8px 20px ${(props) => props.color}40;
`;

export const PlanName = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${colors.gray800};
  margin: 0 0 8px 0;
`;

export const PlanPrice = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: ${colors.gray800};
  margin: 0 0 4px 0;
`;

export const PlanPeriod = styled.p`
  font-size: 14px;
  color: ${colors.gray500};
  margin: 0;
  font-weight: 500;
`;

export const PlanDescription = styled.p`
  font-size: 15px;
  color: ${colors.gray600};
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.6;
  font-weight: 500;
`;

export const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: ${colors.gray700};
`;

export const SelectButton = styled.button<{
  variant: "primary" | "secondary" | "current";
}>`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: ${colors.primary};
          color: ${colors.white};
          &:hover {
            background: ${colors.primaryDark};
          }
        `;
      case "secondary":
        return `
          background: ${colors.gray100};
          color: ${colors.gray700};
          &:hover {
            background: ${colors.gray200};
          }
        `;
      case "current":
        return `
          background: ${colors.success};
          color: ${colors.white};
          cursor: not-allowed;
        `;
    }
  }}
`;

export const SwitchToCommissionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: green;
  color: white;
  border: 1px solid green;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;

  &:hover {
    background: green;
  }
`;

export const CancelSubscriptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${colors.error}15;
  color: ${colors.error};
  border: 1px solid ${colors.error}30;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;

  &:hover {
    background: ${colors.error}25;
  }
`;

export const SubscriptionDetails = styled.div`
  background: ${colors.gray50};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.gray200};
  margin-top: 16px;
`;

export const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.gray200};
  }
`;

export const DetailIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  flex-shrink: 0;
`;

export const DetailText = styled.div`
  flex: 1;
`;

export const DetailLabel = styled.div`
  font-size: 12px;
  color: ${colors.gray600};
  font-weight: 500;
`;

export const DetailValue = styled.div`
  font-size: 14px;
  color: ${colors.gray800};
  font-weight: 600;
`;

export const ModalContainer = styled.div`
  padding: 0;
  width: 100%;
  max-width: 95vw;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  max-height: 95vh;
  overflow-y: auto;
`;

export const ModalHeader = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.primary} 0%,
    ${colors.primaryDark} 100%
  );
  padding: 32px;
  color: ${colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
  }
`;

export const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 12px 0;
  color: ${colors.white};
  position: relative;
`;

export const ModalSubtitle = styled.p`
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
  font-weight: 500;
  position: relative;
`;

export const ModalContent = styled.div`
  padding: 32px;
`;

export const PlanComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 24px;
  align-items: center;
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(
    135deg,
    ${colors.gray50} 0%,
    ${colors.white} 100%
  );
  border-radius: 16px;
  border: 1px solid ${colors.gray200};
`;

export const PlanCard = styled.div`
  text-align: center;
  padding: 20px;
  background: ${colors.white};
  border-radius: 12px;
  border: 2px solid ${colors.gray200};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

export const CurrentPlanCard = styled(PlanCard)`
  border-color: ${colors.primary};
  background: linear-gradient(
    135deg,
    ${colors.primaryLight}15 0%,
    ${colors.white} 100%
  );
  position: relative;
  overflow: hidden;

  &::before {
    content: "CURRENT";
    position: absolute;
    top: 12px;
    left: 12px;
    background: ${colors.primary};
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

export const NewPlanCard = styled(PlanCard)`
  border-color: ${colors.success};
  background: linear-gradient(
    135deg,
    ${colors.success}15 0%,
    ${colors.white} 100%
  );
  position: relative;
  overflow: hidden;

  &::before {
    content: "NEW";
    position: absolute;
    top: 12px;
    left: 12px;
    background: ${colors.success};
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

export const PlanCardTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray600};
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PlanCardName = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.gray800};
  margin: 0 0 8px 0;
`;

export const PlanCardPrice = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${colors.gray800};
  margin: 0 0 4px 0;
`;

export const PlanCardPeriod = styled.p`
  font-size: 13px;
  color: ${colors.gray500};
  margin: 0;
  font-weight: 500;
`;

export const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  background: ${colors.primaryLight};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.2);
`;

export const ContentSection = styled.div`
  margin: 32px 0;
  padding: 24px;
  background: ${colors.gray50};
  border-radius: 16px;
  border: 1px solid ${colors.gray200};
`;

export const ContentTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.gray800};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ContentDisplay = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.gray700};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${colors.gray800};
    margin: 16px 0 8px 0;
  }

  p {
    margin: 12px 0;
  }

  ul,
  ol {
    margin: 12px 0;
    padding-left: 24px;
  }

  li {
    margin: 6px 0;
  }

  strong {
    color: ${colors.gray800};
    font-weight: 600;
  }
`;

export const PaymentSection = styled.div`
  margin: 32px 0;
  padding: 24px;
  background: ${colors.gray50};
  border-radius: 16px;
  border: 1px solid ${colors.gray200};
`;

export const PaymentTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.gray800};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PaymentMethods = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

export const PaymentMethod = styled.label`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 2px solid ${colors.gray300};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${colors.white};

  &:hover {
    border-color: ${colors.primary};
    background: ${colors.primaryLight}15;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    border-color: ${colors.primary};
    background: ${colors.primaryLight}15;
    box-shadow: 0 8px 20px rgba(249, 115, 22, 0.15);
  }
`;

export const PaymentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${colors.primaryLight} 0%,
    ${colors.primary} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.2);
`;

export const PaymentInfo = styled.div`
  flex: 1;
`;

export const PaymentName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.gray800};
  margin-bottom: 4px;
`;

export const PaymentDescription = styled.div`
  font-size: 14px;
  color: ${colors.gray600};
  line-height: 1.4;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${colors.gray200};
`;

export const ModalButton = styled.button<{ variant: "primary" | "secondary" }>`
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%);
          color: ${colors.white};
          box-shadow: 0 4px 15px rgba(249, 115, 22, 0.3);
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(249, 115, 22, 0.4);
          }
        `;
      case "secondary":
        return `
          background: ${colors.gray100};
          color: ${colors.gray700};
          &:hover {
            background: ${colors.gray200};
            transform: translateY(-2px);
          }
        `;
    }
  }}
`;

export const PlanContentSection = styled.div`
  margin: 20px 0;
  padding: 16px;
  background: ${colors.gray50};
  border-radius: 12px;
  border: 1px solid ${colors.gray200};
`;

export const PlanContentTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.gray800};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PlanContentDisplay = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.gray700};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${colors.gray800};
    margin: 12px 0 6px 0;
  }

  p {
    margin: 8px 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  strong {
    color: ${colors.gray800};
    font-weight: 600;
  }
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

export const ConfirmationModalContent = styled.div`
  text-align: center;
  padding: 20px 0;
`;

export const ConfirmationIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: ${colors.primary};
`;

export const ConfirmationTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.gray800};
  margin: 0 0 12px 0;
`;

export const ConfirmationDescription = styled.p`
  font-size: 16px;
  color: ${colors.gray600};
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

export const ConfirmationDetails = styled.div`
  background: ${colors.gray50};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.gray200};
  margin: 20px 0;
`;

export const ConfirmationDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.gray200};
  }
`;

export const ConfirmationDetailLabel = styled.span`
  font-size: 14px;
  color: ${colors.gray600};
  font-weight: 500;
`;

export const ConfirmationDetailValue = styled.span`
  font-size: 14px;
  color: ${colors.gray800};
  font-weight: 600;
`;

export const ShimmerAnimation = styled.div`
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }

  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: shimmer;
  animation-timing-function: linear;
  background: #f6f7f8;
  background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
  background-size: 800px 104px;
  position: relative;
  overflow: hidden;
`;

export const ShimmerDiv = styled(ShimmerAnimation)<{
  width?: string;
  height?: string;
  borderRadius?: string;
  marginBottom?: string;
}>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "20px"};
  border-radius: ${(props) => props.borderRadius || "4px"};
  margin-bottom: ${(props) => props.marginBottom || "0"};
`;
